import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import useDebouncedValue from '../../hooks/useDebouncedValue';

function AsyncSearchSelect({
  id,
  label,
  value,
  onChange,
  loadOptions,
  resolveOption,
  allowEmpty = false,
  emptyLabel = 'Selecione...',
  disabled = false,
  placeholder = 'Buscar...',
  layout = 'stacked',
  hint,
  inputClassName = 'field-input w-full',
}) {
  const listboxId = useId();
  const containerRef = useRef(null);
  const requestIdRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const debouncedSearch = useDebouncedValue(inputValue, 300);
  const [options, setOptions] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const selectableOptions = useMemo(() => {
    const items = options.map((option) => ({
      value: option.value,
      label: option.label,
    }));
    if (allowEmpty) {
      return [{ value: '', label: emptyLabel }, ...items];
    }
    return items;
  }, [allowEmpty, emptyLabel, options]);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setInputValue('');
    setActiveIndex(-1);
  }, []);

  const selectOption = useCallback(
    (option) => {
      onChange({ target: { value: option.value } });
      setSelectedLabel(option.value ? option.label : '');
      closeDropdown();
    },
    [closeDropdown, onChange]
  );

  useEffect(() => {
    if (!value) {
      setSelectedLabel('');
      return undefined;
    }

    const match = selectableOptions.find((option) => option.value === value);
    if (match && match.value) {
      setSelectedLabel(match.label);
      return undefined;
    }

    if (!resolveOption) {
      return undefined;
    }

    let cancelled = false;
    resolveOption(value)
      .then((option) => {
        if (cancelled || !option) {
          return;
        }
        setSelectedLabel(option.label);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [value, selectableOptions, resolveOption]);

  useEffect(() => {
    if (!open || disabled) {
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError('');

    loadOptions(debouncedSearch)
      .then((result) => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setOptions(result || []);
        setActiveIndex(-1);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setOptions([]);
        setError('Não foi possível carregar opções.');
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });

    return undefined;
  }, [debouncedSearch, disabled, loadOptions, open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [closeDropdown, open]);

  const handleInputFocus = () => {
    if (disabled) {
      return;
    }
    setOpen(true);
  };

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
    if (!open) {
      setOpen(true);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDropdown();
      return;
    }

    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => Math.min(current + 1, selectableOptions.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      selectOption(selectableOptions[activeIndex]);
    }
  };

  const displayValue = open
    ? inputValue
    : value
      ? selectedLabel
      : allowEmpty
        ? emptyLabel
        : selectedLabel;
  const inputPlaceholder = open ? placeholder : value ? selectedLabel || placeholder : placeholder;

  const control = (
    <div ref={containerRef} className="relative min-w-0">
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
        }
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={inputPlaceholder}
        autoComplete="off"
        className={`${inputClassName} ${disabled ? 'opacity-60 cursor-not-allowed bg-slate-50' : ''}`}
      />

      {open && !disabled && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 w-full rounded-lg border border-surface-border bg-white shadow-lg overflow-hidden"
        >
          <div className="max-h-60 overflow-y-auto py-1">
            {loading && (
              <p className="px-3 py-2 text-sm text-content-muted">Carregando...</p>
            )}
            {!loading && error && (
              <p className="px-3 py-2 text-sm text-red-600">{error}</p>
            )}
            {!loading && !error && selectableOptions.length === 0 && (
              <p className="px-3 py-2 text-sm text-content-muted">Nenhum resultado encontrado.</p>
            )}
            {!loading &&
              !error &&
              selectableOptions.map((option, index) => (
                <button
                  key={option.value || '__empty__'}
                  id={`${listboxId}-option-${index}`}
                  type="button"
                  role="option"
                  aria-selected={value === option.value}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectOption(option)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                    index === activeIndex || value === option.value
                      ? 'bg-forge-50 text-content-primary'
                      : 'text-content-secondary hover:bg-slate-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );

  if (layout === 'inline') {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0">
        {label && (
          <label htmlFor={id} className="text-sm text-content-muted shrink-0">
            {label}
          </label>
        )}
        <div className="min-w-0 sm:max-w-sm w-full">{control}</div>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-2">
          {label}
        </label>
      )}
      {control}
      {hint && <p className="mt-1.5 text-xs text-content-muted">{hint}</p>}
    </div>
  );
}

export default AsyncSearchSelect;
